import { Iterable, fromJS } from 'immutable';
import { put, take, spawn } from 'redux-saga/effects';

import { remoteRequest, REMOTE_RESPONSE } from './actions.js';

let nextReqId = 0;

const toJS = arg =>
    Iterable.isIterable(arg) && arg.toJS && typeof arg.toJS === 'function' ? arg.toJS() : arg;

/*
 * Convert any Immutable arguments to plain JavaScript objects
 */
export const convertArgsFromImmutable = args => args.map(toJS);

function isResponseFor(reqId, action) {
    return action.type === REMOTE_RESPONSE && action.reqId === reqId;
}

/**
 * Makes a request to the server and returns the response as a Map.
 * @param method - request to make
 * @param args - arguments to pass
 * @returns the response to the request
 */
export function* request(method, ...args) {
    return yield _immutableRequest(method, args);
}

/**
 * Makes a request to the server and returns the response.
 * @param method - request to make
 * @param args - arguments to pass
 * @returns the response to the request
 */
export function* nonImmutableRequest(method, ...args) {
    return yield _nonImmutableRequest(method, args);
}

/**
 * Makes a request to the server and returns the response.
 * @param method - request to make
 * @param args - arguments to pass
 * @param retry - retries the request if the network fails (default true)
 * @param activate - activates the session after the request (default false)
 *                   if true, overrides retry (does not retry)
 * @returns the response to the request
 * @private
 */
export function* _nonImmutableRequest(method, args, retry = true, activate = false) {
    const reqId = nextReqId++;

    // Note: convert arguments from Immutable.js objects if required.
    yield put(remoteRequest(reqId, method, convertArgsFromImmutable(args), retry, activate));
    const { response, error } = yield take(isResponseFor.bind(null, reqId));
    if (error) {
        // If the error is a JavaScript Error, it is from the v2 API bridge, so
        // we want to report it back to the server - just rethrow it (will
        // include stack trace). Note: errors from the ground/v1 server are
        // always objects but not Errors, so this will not catch those.
        // Otherwise, it is a server error so we mark it as fromServer
        // and don't send a log back
        if (error instanceof Error) {
            throw error;
        } else if (error.fromServer === undefined) {
            error.fromServer = true;
        }

        // All Immutable errors must have "type" set, but the cloud server
        // doesn't send this field. Here we set the default value of "type"
        // to be "code", which it does send. If you need to map a cloud error
        // to a particular type you can do so in api-v2.js
        // (see 'PendingWAC' as an example)
        if (error.code && !error.type) {
            error.type = error.code;
        }
        throw fromJS(error);
    }

    return response;
}

/**
 * Makes a request to the server and returns the response.
 * @param method - request to make
 * @param args - arguments to pass
 * @param retry - retries the request if the network fails (default true)
 * @param activate - activates the session after the request (default false)
 *                   if true, overrides retry (does not retry)
 * @returns the response to the request wrapped as a Map
 * @private
 */
export function* _immutableRequest(method, args, retry = true, activate = false) {
    const response = yield _nonImmutableRequest(method, args, retry, activate);
    return fromJS(response);
}

/**
 * notify() sends a message to the server and doesn't expect a response.
 */
export function* notify(method, ...args) {
    yield spawn(request, method, ...args);
}
